import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Form, Button, Message, Input, Icon } from "semantic-ui-react";
import posthog from "posthog-js";
import CommonModal from "../Modal";
import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { Common } from "../../../utils";
import { isEmpty } from "../../Global/CustomFunctions";
import "./ChangePasswordModal.css";

const ChangePasswordModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  // Get hasPassword from userInfo
  const getUserHasPassword = () => {
    try {
      if (!isEmpty(localStorage.userInfo)) {
        const userInfo = JSON.parse(localStorage.userInfo);
        return userInfo.hasPassword === true;
      }
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("changePasswordUserInfo", {
          type: "exception",
          success: false,
          reason: error?.message ?? "Error reading userInfo",
        });
      }
    }
    // Default to true for backward compatibility
    return true;
  };

  const [hasPassword, setHasPassword] = useState(getUserHasPassword());
  const [formData, setFormData] = useState({
    password: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState({
    password: false,
    newPassword: false,
    confirmNewPassword: false,
  });

  // Update hasPassword when modal opens
  useEffect(() => {
    if (open) {
      setHasPassword(getUserHasPassword());
    }
  }, [open]);

  const handleInputChange = (e) => {
    const fieldName = e.target.name;
    const fieldValue = e.target.value;

    setFormData((prev) => {
      const updated = {
        ...prev,
        [fieldName]: fieldValue,
      };

      // Real-time validation for password match
      if (fieldName === "newPassword" || fieldName === "confirmNewPassword") {
        // If both fields have values, check if they match
        if (fieldName === "newPassword" && updated.confirmNewPassword) {
          if (updated.confirmNewPassword.length >= 6 && updated.newPassword !== updated.confirmNewPassword) {
            setErrors((prev) => ({
              ...prev,
              confirmNewPassword: t("app.modals.changePassword.errorPasswordsMatch"),
            }));
          } else if (updated.newPassword === updated.confirmNewPassword) {
            setErrors((prev) => ({
              ...prev,
              confirmNewPassword: "",
            }));
          }
        } else if (fieldName === "confirmNewPassword" && updated.newPassword) {
          if (updated.confirmNewPassword.length >= 6 && updated.newPassword !== updated.confirmNewPassword) {
            setErrors((prev) => ({
              ...prev,
              confirmNewPassword: t("app.modals.changePassword.errorPasswordsMatch"),
            }));
          } else if (updated.newPassword === updated.confirmNewPassword) {
            setErrors((prev) => ({
              ...prev,
              confirmNewPassword: "",
            }));
          }
        }

        // Check if new password is different from current password (only if user has password)
        if (fieldName === "newPassword" && hasPassword && updated.password) {
          if (updated.newPassword === updated.password) {
            setErrors((prev) => ({
              ...prev,
              newPassword: t("app.modals.changePassword.errorNewDifferent"),
            }));
          } else if (updated.newPassword !== updated.password) {
            setErrors((prev) => ({
              ...prev,
              newPassword: prev.newPassword === t("app.modals.changePassword.errorNewDifferent") ? "" : prev.newPassword,
            }));
          }
        }
      }

      // Check if current password is different from new password when current password changes (only if user has password)
      if (fieldName === "password" && hasPassword && updated.newPassword) {
        if (updated.password === updated.newPassword) {
          setErrors((prev) => ({
            ...prev,
            newPassword: "New password must be different from current password",
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            newPassword: prev.newPassword === t("app.modals.changePassword.errorNewDifferent") ? "" : prev.newPassword,
          }));
        }
      }

      return updated;
    });

    // Clear error when user starts typing (for other fields)
    if (errors[fieldName] && fieldName !== "confirmNewPassword" && fieldName !== "newPassword") {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: "",
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Only validate current password if user has a password
    if (hasPassword) {
      if (!formData.password || formData.password.trim() === "") {
        newErrors.password = t("app.modals.changePassword.errorCurrentRequired");
      } else if (formData.password.length < 6) {
        newErrors.password = t("app.modals.changePassword.errorPasswordMin");
      } else if (formData.password.length > 100) {
        newErrors.password = t("app.modals.changePassword.errorPasswordMax");
      }

      // Check if new password is different from current password
      if (formData.password === formData.newPassword) {
        newErrors.newPassword = t("app.modals.changePassword.errorNewDifferent");
      }
    }

    if (!formData.newPassword || formData.newPassword.trim() === "") {
      newErrors.newPassword = t("app.modals.changePassword.errorNewRequired");
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = t("app.modals.changePassword.errorPasswordMin");
    } else if (formData.newPassword.length > 100) {
      newErrors.newPassword = t("app.modals.changePassword.errorPasswordMax");
    }

    if (!formData.confirmNewPassword || formData.confirmNewPassword.trim() === "") {
      newErrors.confirmNewPassword = t("app.modals.changePassword.errorConfirmRequired");
    } else if (formData.confirmNewPassword.length < 6) {
      newErrors.confirmNewPassword = t("app.modals.changePassword.errorPasswordMin");
    } else if (formData.confirmNewPassword.length > 100) {
      newErrors.confirmNewPassword = t("app.modals.changePassword.errorPasswordMax");
    } else if (formData.newPassword !== formData.confirmNewPassword) {
      newErrors.confirmNewPassword = t("app.modals.changePassword.errorPasswordsMatch");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        NewPassword: formData.newPassword.trim(),
        ConfirmNewPassword: formData.confirmNewPassword.trim(),
      };

      // Only include current password if user has a password
      if (hasPassword) {
        payload.Password = formData.password.trim();
      }

      const response = await axiosBasic.post(
        config.REST_API.Account.ChangePassword,
        payload
      );

      if (response.data && response.data.success !== false) {
        Common.showSuccessMessage(
          response.data.message || t("app.modals.changePassword.success")
        );
        // Reset form
        setFormData({
          password: "",
          newPassword: "",
          confirmNewPassword: "",
        });
        setErrors({});
        onClose();
      } else {
        Common.showErrorMessage(
          response.data?.message || t("app.modals.changePassword.errorFailed")
        );
      }
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("changePassword", {
          type: "exception",
          success: false,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
          status: error?.response?.status,
        });
      }
      Common.showErrorMessage(
        error?.response?.data?.message ||
        t("app.modals.changePassword.errorGeneric")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        password: "",
        newPassword: "",
        confirmNewPassword: "",
      });
      setErrors({});
      setShowPassword({
        password: false,
        newPassword: false,
        confirmNewPassword: false,
      });
      onClose();
    }
  };

  return (
    <CommonModal
      open={open}
      onClose={handleClose}
      title={t("app.modals.changePassword.title")}
      subtitle={hasPassword
        ? t("app.modals.changePassword.subtitleWithCurrent")
        : t("app.modals.changePassword.subtitleNoCurrent")}
      size="small"
      className="change-password-modal"
    >
      <Form onSubmit={handleSubmit}>
        {hasPassword && (
          <Form.Field required error={!!errors.password}>
            <label>{t("app.modals.changePassword.currentPassword")}</label>
            <div style={{ position: "relative" }}>
              <Input
                name="password"
                type={showPassword.password ? "text" : "password"}
                value={formData.password}
                onChange={handleInputChange}
                placeholder={t("app.modals.changePassword.placeholderCurrent")}
                disabled={isSubmitting}
                style={{ paddingRight: "40px" }}
                maxLength={100}
              />
              <button
                type="button"
                onClick={() => !isSubmitting && togglePasswordVisibility("password")}
                disabled={isSubmitting}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: "#6b7280",
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  padding: "5px",
                  display: "flex",
                  alignItems: "center",
                  opacity: isSubmitting ? 0.5 : 1,
                }}
                title={showPassword.password ? t("app.modals.changePassword.hidePassword") : t("app.modals.changePassword.showPassword")}
              >
                <Icon
                  name={showPassword.password ? "eye slash" : "eye"}
                  style={{ margin: 0 }}
                />
              </button>
            </div>
            {errors.password && (
              <Message negative size="tiny">
                {errors.password}
              </Message>
            )}
          </Form.Field>
        )}

        <Form.Field required error={!!errors.newPassword}>
          <label>{t("app.modals.changePassword.newPassword")}</label>
          <div style={{ position: "relative" }}>
            <Input
              name="newPassword"
              type={showPassword.newPassword ? "text" : "password"}
              value={formData.newPassword}
              onChange={handleInputChange}
              placeholder={t("app.modals.changePassword.placeholderNew")}
              disabled={isSubmitting}
              style={{ paddingRight: "40px" }}
              maxLength={100}
            />
            <button
              type="button"
              onClick={() => !isSubmitting && togglePasswordVisibility("newPassword")}
              disabled={isSubmitting}
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                color: "#6b7280",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                padding: "5px",
                display: "flex",
                alignItems: "center",
                opacity: isSubmitting ? 0.5 : 1,
              }}
              title={showPassword.newPassword ? t("app.modals.changePassword.hidePassword") : t("app.modals.changePassword.showPassword")}
            >
              <Icon
                name={showPassword.newPassword ? "eye slash" : "eye"}
                style={{ margin: 0 }}
              />
            </button>
          </div>
          {errors.newPassword && (
            <Message negative size="tiny">
              {errors.newPassword}
            </Message>
          )}
        </Form.Field>

        <Form.Field required error={!!errors.confirmNewPassword}>
          <label>{t("app.modals.changePassword.confirmNewPassword")}</label>
          <div style={{ position: "relative" }}>
            <Input
              name="confirmNewPassword"
              type={showPassword.confirmNewPassword ? "text" : "password"}
              value={formData.confirmNewPassword}
              onChange={handleInputChange}
              placeholder={t("app.modals.changePassword.placeholderConfirm")}
              disabled={isSubmitting}
              style={{ paddingRight: "40px" }}
              maxLength={100}
            />
            <button
              type="button"
              onClick={() => !isSubmitting && togglePasswordVisibility("confirmNewPassword")}
              disabled={isSubmitting}
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                color: "#6b7280",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                padding: "5px",
                display: "flex",
                alignItems: "center",
                opacity: isSubmitting ? 0.5 : 1,
              }}
              title={showPassword.confirmNewPassword ? t("app.modals.changePassword.hidePassword") : t("app.modals.changePassword.showPassword")}
            >
              <Icon
                name={showPassword.confirmNewPassword ? "eye slash" : "eye"}
                style={{ margin: 0 }}
              />
            </button>
          </div>
          {errors.confirmNewPassword && (
            <Message negative size="tiny">
              {errors.confirmNewPassword}
            </Message>
          )}
        </Form.Field>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginTop: "20px",
          }}
        >
          <Button type="button" onClick={handleClose} disabled={isSubmitting}>
            {t("app.modals.changePassword.cancel")}
          </Button>
          <Button
            type="submit"
            primary
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {t("app.modals.changePassword.submit")}
          </Button>
        </div>
      </Form>
    </CommonModal>
  );
};

export default ChangePasswordModal;
